import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { ArrowLeft, Pin, Pencil, Trash2, Check, X, CornerDownRight, Send, Code } from 'lucide-react';
import FileAttachments from '../components/FileAttachments';

async function uploadImage(file) {
  const fd = new FormData();
  fd.append('image', file);
  const r = await api.post('/boards/upload', fd);
  return r.data.url;
}

function CommentSection({ noticeId }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const replyInputRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('paper_work_user') || '{}');
  const isSuperadmin = user.role === 'superadmin';

  const load = async () => {
    const r = await api.get(`/notices/${noticeId}/comments`);
    setComments(r.data);
  };

  useEffect(() => { load(); }, [noticeId]);

  const submit = async () => {
    if (!text.trim()) return;
    await api.post(`/notices/${noticeId}/comments`, { content: text.trim() });
    setText('');
    load();
  };

  const submitReply = async (parentId) => {
    if (!replyText.trim()) return;
    await api.post(`/notices/${noticeId}/comments`, { content: replyText.trim(), parentId });
    setReplyText('');
    setReplyTo(null);
    load();
  };

  const del = async (commentId) => {
    if (!confirm('삭제하시겠습니까?')) return;
    await api.delete(`/notices/${noticeId}/comments/${commentId}`);
    load();
  };

  const startReply = (comment) => {
    setReplyTo(comment.id);
    setReplyText('');
    setTimeout(() => replyInputRef.current?.focus(), 50);
  };

  const fmt = (d) => {
    const dt = new Date(d);
    return dt.toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="border-t border-gray-800 flex-shrink-0">
      {comments.length > 0 && (
        <div className="px-5 pt-4 pb-2 space-y-3 max-h-72 overflow-y-auto">
          {comments.map(c => (
            <div key={c.id}>
              <div className="group flex items-start gap-2.5">
                <div className="w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0 text-xs text-gray-300 font-medium">
                  {(c.author?.name || c.author?.username || '?')[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-white text-xs font-semibold">{c.author?.name || c.author?.username}</span>
                    <span className="text-gray-600 text-[10px]">{fmt(c.createdAt)}</span>
                  </div>
                  <p className="text-gray-300 text-sm mt-0.5 whitespace-pre-wrap break-words">{c.content}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <button
                      onClick={() => replyTo === c.id ? setReplyTo(null) : startReply(c)}
                      className="text-gray-600 hover:text-gray-400 text-[11px] transition-colors"
                    >
                      답글
                    </button>
                    {(c.authorId === user.id || isSuperadmin) && (
                      <button onClick={() => del(c.id)} className="text-gray-700 hover:text-red-400 text-[11px] opacity-0 group-hover:opacity-100 transition-opacity">
                        삭제
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {c.replies?.map(r => (
                <div key={r.id} className="group flex items-start gap-2.5 mt-2 ml-9">
                  <CornerDownRight size={12} className="text-gray-700 flex-shrink-0 mt-1" />
                  <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0 text-[10px] text-gray-300 font-medium">
                    {(r.author?.name || r.author?.username || '?')[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-white text-xs font-semibold">{r.author?.name || r.author?.username}</span>
                      <span className="text-gray-600 text-[10px]">{fmt(r.createdAt)}</span>
                    </div>
                    <p className="text-gray-300 text-sm mt-0.5 whitespace-pre-wrap break-words">{r.content}</p>
                    {(r.authorId === user.id || isSuperadmin) && (
                      <button onClick={() => del(r.id)} className="text-gray-700 hover:text-red-400 text-[11px] opacity-0 group-hover:opacity-100 transition-opacity mt-0.5">
                        삭제
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {replyTo === c.id && (
                <div className="flex items-center gap-2 mt-2 ml-9 pl-5">
                  <input
                    ref={replyInputRef}
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitReply(c.id); } }}
                    placeholder={`${c.author?.name || c.author?.username}에게 답글...`}
                    className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-brand placeholder-gray-600"
                  />
                  <button onClick={() => submitReply(c.id)} className="p-1.5 text-brand hover:text-brand/80">
                    <Send size={14} />
                  </button>
                  <button onClick={() => setReplyTo(null)} className="p-1.5 text-gray-600 hover:text-white">
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2.5 px-5 py-3 border-t border-gray-800/60">
        <div className="w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0 text-xs text-gray-300 font-medium">
          {(user.name || user.username || '?')[0]}
        </div>
        <input
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
          placeholder="댓글을 입력하세요..."
          className="flex-1 bg-gray-800/60 border border-gray-800 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand/50 placeholder-gray-600"
        />
        <button onClick={submit} disabled={!text.trim()} className="p-1.5 text-brand hover:text-brand/80 disabled:opacity-30">
          <Send size={15} />
        </button>
      </div>
    </div>
  );
}

export default function NoticeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [notice, setNotice] = useState(null);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [isPinned, setIsPinned] = useState(false);
  const [rawMode, setRawMode] = useState(false);
  const [rawContent, setRawContent] = useState('');

  const scrollAreaRef = useRef(null);
  const editableRef = useRef(null);
  const fileInputRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('paper_work_user') || '{}');
  const isSuperadmin = user.role === 'superadmin';

  const load = async () => {
    const r = await api.get(`/notices/${id}`);
    setNotice(r.data);
    setTitle(r.data.title);
    setIsPinned(r.data.isPinned);
  };

  useEffect(() => { load(); }, [id]);

  // WYSIWYG 모드 진입 시 innerHTML 주입
  useEffect(() => {
    if (editing && !rawMode && editableRef.current && notice) {
      editableRef.current.innerHTML = notice.content || '';
      editableRef.current.focus();
      scrollAreaRef.current?.scrollTo({ top: 0 });
    }
  }, [editing]);

  // rawMode → WYSIWYG 전환 시 innerHTML 복원
  const switchToWysiwyg = (html) => {
    setRawMode(false);
    setTimeout(() => {
      if (editableRef.current) {
        editableRef.current.innerHTML = html;
        editableRef.current.focus();
      }
    }, 0);
  };

  const startEdit = () => {
    setRawContent(notice.content || '');
    setRawMode(false);
    setEditing(true);
  };

  const save = async () => {
    const content = rawMode ? rawContent : (editableRef.current?.innerHTML || '');
    await api.put(`/notices/${id}`, { title, content, isPinned });
    setEditing(false);
    setRawMode(false);
    load();
  };

  const cancelEdit = () => {
    setEditing(false);
    setRawMode(false);
    setTitle(notice.title);
    setIsPinned(notice.isPinned);
  };

  const toggleRawMode = () => {
    if (rawMode) {
      switchToWysiwyg(rawContent);
    } else {
      setRawContent(editableRef.current?.innerHTML || '');
      setRawMode(true);
    }
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;
        const url = await uploadImage(file);
        document.execCommand('insertImage', false, url);
        return;
      }
    }
  };

  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    for (const f of files) {
      if (!f.type.startsWith('image/')) continue;
      const url = await uploadImage(f);
      editableRef.current?.focus();
      document.execCommand('insertImage', false, url);
    }
    e.target.value = '';
  };

  const remove = async () => {
    if (!confirm('삭제하시겠습니까?')) return;
    await api.delete(`/notices/${id}`);
    navigate('/notices');
  };

  const fmt = (d) => new Date(d).toLocaleString('ko-KR');

  if (!notice) return <div className="text-gray-600 text-sm p-8">불러오는 중...</div>;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* 헤더 */}
      <div className="flex-shrink-0 border-b border-gray-800 bg-gray-950">
        <div className="flex items-center justify-between px-4 py-2.5 gap-3">
          <button onClick={() => navigate('/notices')}
            className="flex items-center gap-1.5 text-gray-500 hover:text-white text-sm transition-colors flex-shrink-0">
            <ArrowLeft size={15} /> 목록
          </button>

          <div className="flex-1 min-w-0">
            {editing ? (
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                autoFocus
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm font-semibold focus:outline-none focus:border-brand"
              />
            ) : (
              <div className="flex items-center gap-2 min-w-0">
                {notice.isPinned && <Pin size={13} className="text-brand flex-shrink-0" />}
                <h1 className="text-white font-bold text-base truncate">{notice.title}</h1>
              </div>
            )}
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            {editing ? (
              <>
                <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer mr-1">
                  <input type="checkbox" checked={isPinned} onChange={e => setIsPinned(e.target.checked)} className="accent-brand" />
                  고정
                </label>
                <button
                  onClick={toggleRawMode}
                  title="HTML 편집 모드 전환"
                  className={`flex items-center gap-1 px-2 py-1 rounded text-xs border transition-colors ${rawMode ? 'border-brand text-brand bg-brand/10' : 'border-gray-700 text-gray-500 hover:text-gray-300 hover:border-gray-600'}`}
                >
                  <Code size={11} /> HTML
                </button>
                <button onClick={save} className="flex items-center gap-1 px-3 py-1.5 bg-brand text-white text-xs rounded-lg hover:bg-brand/80">
                  <Check size={12} /> 저장
                </button>
                <button onClick={cancelEdit} className="p-1.5 text-gray-500 hover:text-white">
                  <X size={14} />
                </button>
              </>
            ) : (
              isSuperadmin && (
                <>
                  <button onClick={startEdit} className="p-1.5 text-gray-500 hover:text-white rounded-md hover:bg-gray-800">
                    <Pencil size={14} />
                  </button>
                  <button onClick={remove} className="p-1.5 text-gray-500 hover:text-red-400 rounded-md hover:bg-gray-800">
                    <Trash2 size={14} />
                  </button>
                </>
              )
            )}
          </div>
        </div>

        <div className="px-5 pb-2.5 flex items-center gap-3">
          <span className="text-gray-600 text-xs">
            {notice.author?.name || notice.author?.username} · {fmt(notice.createdAt)}
          </span>
          {editing && !rawMode && (
            <span className="text-xs text-brand/60">클릭하여 직접 편집</span>
          )}
        </div>
      </div>

      {/* 본문 */}
      <div ref={scrollAreaRef} className="flex-1 overflow-y-auto">
        {!editing ? (
          notice.content
            ? <div className="rich-view px-6 py-5" dangerouslySetInnerHTML={{ __html: notice.content }} />
            : <p className="text-gray-600 text-sm px-6 py-8">내용 없음</p>
        ) : rawMode ? (
          <textarea
            value={rawContent}
            onChange={e => setRawContent(e.target.value)}
            className="w-full h-full min-h-[500px] bg-gray-900 text-gray-300 font-mono text-xs p-5 resize-none focus:outline-none border-0"
            spellCheck={false}
          />
        ) : (
          <div
            ref={editableRef}
            contentEditable
            suppressContentEditableWarning
            onPaste={handlePaste}
            className="max-w-none px-6 py-5 focus:outline-none"
            style={{ minHeight: '200px', cursor: 'text', outline: 'none' }}
          />
        )}
      </div>

      {/* 첨부 파일 */}
      <FileAttachments
        entityType="notice"
        entityId={parseInt(id)}
        editor={null}
        canUpload={editing && !rawMode}
        onImageInsert={url => {
          editableRef.current?.focus();
          document.execCommand('insertImage', false, url);
        }}
      />

      {/* 댓글 */}
      <CommentSection noticeId={id} />

      <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileSelect} />
    </div>
  );
}
